import * as React from 'react';
import {Popover, WithStyles, withStyles} from '@material-ui/core';
import {Add, DeleteOutline, EventOutlined, Remove} from '@material-ui/icons';
import TranslateNew from '@translate/TranslateNew';
import {IVaccination} from '@app-types/websocket/sharedinfo.types';
import Button from '@components/common/button/Button';
import IconButton from '@components/common/iconButton/iconButton.component';
import Dialog from '@components/common/dialog/dialog.component';
import VVFullDatePicker from '@components/common/timepicker/VVFullDatePicker';
import {formatDate} from '@helpers/DateHelper';
import {ChildInfoVaccinationsStyle as styles} from './ChildInfoVaccinations.style';

const tn = TranslateNew.getInstance();
let nextEditorId = 0;

interface IProps extends WithStyles<typeof styles> {
  vaccinations: IVaccination[];
  showVaccDetails: boolean;
  hide: () => void;
  save: (vaccinations: IVaccination[]) => void;
}

interface IVaccinationDraft {
  key: number;
  name: string;
  doses: string;
  lastDose: string;
  todoId?: number;
}

interface IState {
  vaccinations: IVaccinationDraft[];
  showValidation: boolean;
  removed?: {vaccination: IVaccinationDraft; index: number};
  dateEditor?: {key: number; anchor: HTMLButtonElement; date: Date};
}

// Match the integer field in the existing vaccination API.
const MAX_DOSES = 2147483647;
const validDoses = (value: string) => /^\d+$/.test(value) && Number(value) <= MAX_DOSES;

export class ChildInfoVaccinations extends React.Component<IProps, IState> {
  private editorId = `vaccinations-${++nextEditorId}`;
  private nextKey = 0;
  private form = React.createRef<HTMLFormElement>();
  private nameInputs: {[key: number]: HTMLInputElement | null} = {};

  constructor(props: IProps) {
    super(props);
    this.state = {vaccinations: this.makeDrafts(props.vaccinations), showValidation: false};
  }

  public componentDidUpdate(previous: IProps) {
    if ((!previous.showVaccDetails && this.props.showVaccDetails)
      || (!this.props.showVaccDetails && previous.vaccinations !== this.props.vaccinations)) {
      this.setState({
        vaccinations: this.makeDrafts(this.props.vaccinations),
        showValidation: false,
        removed: undefined,
        dateEditor: undefined,
      });
    }
  }

  public render() {
    const {classes} = this.props;
    return (
      <>
        <Dialog
          open={this.props.showVaccDetails}
          onClose={this.props.hide}
          fullWidth={true}
          title={tn.lang.childInfoVaccinations}
          classes={{paper: classes.paper, dialogContent: classes.content, dialogActions: classes.footer}}
          content={
            <form id={this.editorId} ref={this.form} onSubmit={this.save} noValidate={true} className={classes.cards}>
              {this.state.vaccinations.length === 0 && (
                <p className={classes.empty}>{tn.lang.vaccineEmpty}</p>
              )}
              {this.state.vaccinations.map(vaccination => this.renderCard(vaccination))}
              {this.state.removed && (
                <div className={classes.removed} role="status">
                  <span>{tn.lang.vaccineRemoved}</span>
                  <Button type="button" className={classes.action} color="primary" onClick={this.undoRemove}>
                    {tn.lang.undo}
                  </Button>
                </div>
              )}
              <Button type="button" variant="outlined" color="primary" className={classes.addButton}
                onClick={this.addVaccination}>
                <Add />{tn.lang.vaccineAdd}
              </Button>
            </form>
          }
          actions={
            <>
              <Button type="button" className={classes.action} onClick={this.props.hide}>{tn.lang.cancel}</Button>
              <Button type="submit" form={this.editorId} onClick={this.save}
                variant="contained" color="primary" className={classes.action}>
                {tn.lang.save}
              </Button>
            </>
          }
        />
        {this.state.dateEditor && this.renderDatePicker()}
      </>
    );
  }

  private renderCard(vaccination: IVaccinationDraft) {
    const {classes} = this.props;
    const id = `${this.editorId}-${vaccination.key}`;
    const nameInvalid = this.state.showValidation && vaccination.name.trim() === '';
    const dosesInvalid = this.state.showValidation && !validDoses(vaccination.doses);
    const name = vaccination.name.trim() || tn.lang.vaccineNew;

    return (
      <section key={vaccination.key} className={classes.card} aria-label={name}>
        <div className={classes.cardHeader}>
          <div className={classes.field}>
            <label htmlFor={`${id}-name`} className={classes.label}>{tn.lang.childInfoVaccination}</label>
            <input id={`${id}-name`} ref={input => {this.nameInputs[vaccination.key] = input;}}
              className={`${classes.input} ${classes.nameInput}`} type="text" autoComplete="off"
              placeholder={tn.lang.vaccineNamePlaceholder} value={vaccination.name}
              aria-invalid={nameInvalid} aria-describedby={nameInvalid ? `${id}-name-error` : undefined}
              onChange={event => this.updateVaccination(vaccination.key, {name: event.target.value})} />
            {nameInvalid && <span id={`${id}-name-error`} className={classes.error}>{tn.lang.vaccineNameRequired}</span>}
          </div>
          <IconButton type="button" className={classes.removeButton} aria-label={`${tn.lang.delete}: ${name}`}
            onClick={() => this.removeVaccination(vaccination.key)}>
            <DeleteOutline />
          </IconButton>
        </div>
        <div className={classes.fields}>
          <div className={classes.field}>
            <label htmlFor={`${id}-doses`} className={classes.label}>{tn.lang.vaccineDosesReceived}</label>
            <div className={classes.stepper}>
              <IconButton type="button" className={classes.stepButton}
                aria-label={`${tn.lang.vaccineDecreaseDose}: ${name}`} disabled={Number(vaccination.doses) <= 0}
                onClick={() => this.stepDoses(vaccination.key, -1)}><Remove /></IconButton>
              <input id={`${id}-doses`} className={classes.doseInput} type="number" min="0" step="1"
                inputMode="numeric" value={vaccination.doses} aria-invalid={dosesInvalid}
                aria-describedby={dosesInvalid ? `${id}-doses-error` : undefined}
                onFocus={event => event.target.select()}
                onChange={event => this.updateVaccination(vaccination.key, {doses: event.target.value})} />
              <IconButton type="button" className={classes.stepButton}
                aria-label={`${tn.lang.vaccineIncreaseDose}: ${name}`}
                disabled={Number(vaccination.doses) >= MAX_DOSES}
                onClick={() => this.stepDoses(vaccination.key, 1)}><Add /></IconButton>
            </div>
            {dosesInvalid && <span id={`${id}-doses-error`} className={classes.error}>{tn.lang.vaccineDosesInvalid}</span>}
          </div>
          <div className={classes.field}>
            <label id={`${id}-date-label`} htmlFor={`${id}-date`} className={classes.label}>{tn.lang.vaccineLastDose}</label>
            <Button id={`${id}-date`} type="button" variant="outlined" fullWidth={true} className={classes.dateButton}
              aria-labelledby={`${id}-date-label ${id}-date-value`} aria-haspopup="dialog"
              aria-expanded={!!this.state.dateEditor && this.state.dateEditor.key === vaccination.key}
              onClick={event => this.setState({dateEditor: {
                key: vaccination.key, anchor: event.currentTarget,
                date: vaccination.lastDose ? new Date(`${vaccination.lastDose}T12:00:00`) : new Date(),
              }})}>
              <EventOutlined />
              <span id={`${id}-date-value`}>{vaccination.lastDose || tn.lang.vaccineSelectDate}</span>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  private renderDatePicker() {
    const {classes} = this.props;
    const editor = this.state.dateEditor!;
    const labelId = `${this.editorId}-${editor.key}-date-label`;
    return (
      <Popover open={this.props.showVaccDetails} anchorEl={editor.anchor} onClose={this.closeDatePicker}
        disableScrollLock={true} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        transformOrigin={{vertical: 'top', horizontal: 'center'}}
        PaperProps={{role: 'dialog', 'aria-labelledby': labelId}}>
        <VVFullDatePicker open={true} date={editor.date}
          minYear={1900} maxYear={Math.max(new Date().getFullYear() + 5, editor.date.getFullYear())}
          setDate={date => this.setState({dateEditor: {...editor, date}})} />
        <div className={classes.dateActions}>
          <Button type="button" onClick={() => this.commitDate('')}>{tn.lang.clear}</Button>
          <Button type="button" onClick={this.closeDatePicker}>{tn.lang.cancel}</Button>
          <Button type="button" variant="contained" color="primary"
            onClick={() => this.commitDate(formatDate(editor.date))}>{tn.lang.okay}</Button>
        </div>
      </Popover>
    );
  }

  private closeDatePicker = () => this.setState({dateEditor: undefined});

  private commitDate = (lastDose: string) => {
    this.updateVaccination(this.state.dateEditor!.key, {lastDose});
    this.closeDatePicker();
  };

  private makeDrafts = (vaccinations: IVaccination[]): IVaccinationDraft[] =>
    vaccinations.map(vaccination => ({...vaccination, key: ++this.nextKey, doses: String(vaccination.doses)}));

  private updateVaccination = (key: number, update: Partial<IVaccinationDraft>) => {
    this.setState(state => ({
      vaccinations: state.vaccinations.map(vaccination => vaccination.key === key ? {...vaccination, ...update} : vaccination),
    }));
  };

  private stepDoses = (key: number, delta: number) => {
    this.setState(state => ({
      vaccinations: state.vaccinations.map(vaccination => {
        if (vaccination.key !== key) {return vaccination;}
        const doses = validDoses(vaccination.doses) ? Number(vaccination.doses) : 0;
        return {...vaccination, doses: String(Math.max(0, Math.min(MAX_DOSES, doses + delta)))};
      }),
    }));
  };

  private addVaccination = () => {
    const key = ++this.nextKey;
    this.setState(state => ({
      vaccinations: [...state.vaccinations, {key, name: '', doses: '1', lastDose: ''}],
    }), () => {
      const input = this.nameInputs[key];
      if (input) {
        input.focus();
        if (typeof input.scrollIntoView === 'function') {input.scrollIntoView({block: 'nearest', behavior: 'smooth'});}
      }
    });
  };

  private removeVaccination = (key: number) => {
    this.setState(state => {
      const index = state.vaccinations.findIndex(vaccination => vaccination.key === key);
      return {
        removed: {vaccination: state.vaccinations[index], index},
        vaccinations: state.vaccinations.filter(vaccination => vaccination.key !== key),
      };
    });
  };

  private undoRemove = () => {
    this.setState(state => {
      const vaccinations = state.vaccinations.slice();
      if (state.removed) {vaccinations.splice(state.removed.index, 0, state.removed.vaccination);}
      return {vaccinations, removed: undefined};
    });
  };

  private save = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (this.state.vaccinations.some(vaccination => vaccination.name.trim() === '' || !validDoses(vaccination.doses))) {
      this.setState({showValidation: true}, () => {
        const invalid = this.form.current && this.form.current.querySelector<HTMLInputElement>('[aria-invalid="true"]');
        if (invalid) {invalid.focus();}
      });
      return;
    }
    this.props.save(this.state.vaccinations.map(({name, doses, lastDose, todoId}) => ({
      name: name.trim(), doses: Number(doses), lastDose, todoId,
    })));
  };
}

export default withStyles(styles)(ChildInfoVaccinations);



// WEBPACK FOOTER //
// ./src/components/childinfo/ChildInfoVaccinations.tsx